import { InputNumber } from 'components/InputNumber'
import { Column } from 'components/v2/Column'
import { Row } from 'components/v2/Row'
import { ReactElement } from 'react'
import { useFormikContext } from 'formik'
import styled from 'styled-components'
import { IEditBookingValues, ILuggageAncillaries } from 'features/Bookings/components/EditBooking/types'
import { Input } from 'components/Input'
import { FormattedMessage, useIntl } from 'react-intl'
import { safeFormatMessage } from 'utils/intl'
import { ancillaryTypeMessages } from 'features/Ancillaries/components/NewAncillaryForm/messages'

const Subtitle = styled.div`
  font-weight: 500;
  margin-bottom: 10px;
`
interface IAncillaryItemProps {
  ancillary: ILuggageAncillaries
  colWidth: number
  ticketIndex: number
  handleChange: (ticketIndex: number, ancillaryId: string, qty: number) => void
}

const AncillaryItem = ({ ancillary, colWidth, ticketIndex, handleChange }: IAncillaryItemProps): ReactElement => {
  const intl = useIntl()

  return (
    <Column key={ancillary.ancillaryId} width={`${colWidth}%`}>
      <InputNumber
        key={ancillary.ancillaryId}
        value={ancillary.count}
        onChange={(value: string): void => handleChange(ticketIndex, ancillary.ancillaryId, Number(value))}
        label={safeFormatMessage({ intl, key: ancillary.type, messages: ancillaryTypeMessages })}
        type="number"
      />
    </Column>
  )
}

const AncillariesForm = (): ReactElement | null => {
  const { values, setFieldValue } = useFormikContext<IEditBookingValues>()
  const departureTickets = values.departureTicketsWithLuggage
  const returnTickets = values.returnTicketsWithLuggage
  const handleUpdateDepartureCount = (ticketIndex: number, ancillaryId: string, qty: number): void => {
    const ticket = departureTickets[ticketIndex]
    const newLuggageAncillaries = ticket.luggageAncillaries.map((item) => {
      if (item.ancillaryId === ancillaryId) {
        return { ...item, count: qty }
      }
      return item
    })
    setFieldValue(`departureTicketsWithLuggage[${ticketIndex}].luggageAncillaries`, newLuggageAncillaries)
  }

  const handleUpdateReturnCount = (ticketIndex: number, ancillaryId: string, qty: number): void => {
    const ticket = returnTickets[ticketIndex]
    const newLuggageAncillaries = ticket.luggageAncillaries.map((item) => {
      if (item.ancillaryId === ancillaryId) {
        return { ...item, count: qty }
      }
      return item
    })

    setFieldValue(`returnTicketsWithLuggage[${ticketIndex}].luggageAncillaries`, newLuggageAncillaries)
  }
  return (
    <>
      <Row>
        <Column>
          <Subtitle>
            <FormattedMessage id="ancillariesForm.departure" defaultMessage="Departure" />
          </Subtitle>
        </Column>
      </Row>
      {departureTickets.map((ticket, ticketIndex) => {
        const ancillaries = ticket.luggageAncillaries ?? []
        const colWidth = 100 / (ancillaries.length + 2)
        return (
          <Row key={ticket.id}>
            <Column width={`${colWidth}%`}>
              <Input
                label={<FormattedMessage id="ancillariesForm.firstName" defaultMessage="First Name" />}
                value={ticket.passenger?.firstName}
                disabled
                readOnly
              />
            </Column>
            <Column width={`${colWidth}%`}>
              <Input
                label={<FormattedMessage id="ancillariesForm.lastName" defaultMessage="Last Name" />}
                value={ticket.passenger?.lastName}
                disabled
                readOnly
              />
            </Column>
            {ancillaries.map((ancillary) => {
              return (
                <AncillaryItem
                  key={ancillary.ancillaryId}
                  ancillary={ancillary}
                  colWidth={colWidth}
                  ticketIndex={ticketIndex}
                  handleChange={handleUpdateDepartureCount}
                />
              )
            })}
          </Row>
        )
      })}
      {returnTickets.length > 0 ? (
        <>
          <Row>
            <Column>
              <Subtitle>
                <FormattedMessage id="ancillariesForm.return" defaultMessage="Return" />
              </Subtitle>
            </Column>
          </Row>
          {returnTickets.map((ticket, ticketIndex) => {
            const ancillaries = ticket.luggageAncillaries ?? []
            const colWidth = 100 / (ancillaries.length + 2)

            return (
              <Row key={ticket.id}>
                <Column width={`${colWidth}%`}>
                  <Input
                    label={<FormattedMessage id="ancillariesForm.firstName" defaultMessage="First Name" />}
                    value={ticket.passenger?.firstName}
                    disabled
                    readOnly
                  />
                </Column>
                <Column width={`${colWidth}%`}>
                  <Input
                    label={<FormattedMessage id="ancillariesForm.lastName" defaultMessage="Last Name" />}
                    value={ticket.passenger?.lastName}
                    disabled
                    readOnly
                  />
                </Column>
                {ancillaries.map((ancillary) => {
                  return (
                    <AncillaryItem
                      key={ancillary.ancillaryId}
                      ancillary={ancillary}
                      colWidth={colWidth}
                      ticketIndex={ticketIndex}
                      handleChange={handleUpdateReturnCount}
                    />
                  )
                })}
              </Row>
            )
          })}
        </>
      ) : null}
    </>
  )
}

export default AncillariesForm
